import React, { useCallback, useEffect, useRef, useState } from "react";
import "./reportForm.scss";
import AuthContainer from "../../../store/container/AuthContainer";
import { AuthReducerProps } from "../../../store/reducers/authReducer";
import { CaseDetail } from "../../../models/CaseDetail/caseDetail.model";
import {
  Button,
  Col,
  Menu,
  Popover,
  Row,
  Modal,
  Input,
  Tooltip,
  Spin,
  Progress,
} from "antd";
import AppLoader from "../AppLoader";
import { Report } from "../../../models/Report/report.model";
import { Form, Formik, FormikValues } from "formik";
import Editor from "../Editor";
import { reportFormValidation } from "./reportFormValidation";
import { ReportTemplate } from "../../../models/ReportTemplate/reportTemplate.model";
import {
  getDropdownOptions,
  IDropdownOptions,
} from "../../utils/dropdownUtils";
import ReportTemplateService from "../../../services/ReportTemplate/reportTemplate.service";
import DropdownField from "../DropdownField";
import ReportService, {
  resolveAiReportingAdminId,
} from "../../../services/Report/report.service";
import {
  EyeOutlined,
  DownloadOutlined,
  SoundOutlined,
  AudioOutlined,
  AudioMutedOutlined,
  QuestionCircleOutlined,
  RobotOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import RadAiAssistant from "../RadAiAssistant";
import { DownloadFileTypeEnum } from "../../../enums/downloadFileType.enum";
import AdminCaseService from "../../../services/Case/AdminCase/adminCase.service";
import { UserRoleEnum } from "../../../enums/userRole.enum";
import DoctorCaseService from "../../../services/Case/DoctorCase/doctorCase.service";
import { Doctor } from "../../../models/Doctor/doctor.model";
import { Admin } from "../../../models/Admin/admin.model";
import { Case } from "../../../models/Case/case.model";
import { CaseStatusEnum } from "../../../enums/caseStatus.enum";
import Notification from "../Notification";
import { NotificationTypes } from "../../../enums/notificationTypes";

import { setImageWidthFromFigure } from "../../utils/dataFormatConverter";
import ApiRoutes from "../../../routes/routeConstants/apiRoutes";

declare var MediaRecorder: any;
let socket: WebSocket | null = null;
const aiDiagnosisTypes = ["US"]

interface ReportFormProps extends AuthReducerProps {
  caseDetailId: number;
  onPublish: () => void;
  user: Doctor | Admin;
  onClose?: () => void;
  fromViewer?: boolean;
  content: string;
  values: { content: string }; // Assuming your form has a 'content' field
  setFieldValue: (field: string, value: string) => void;
  errors: { content: string };
}

interface OrganApiResponse {
  data: { id: number; value: string }[];
  message: string;
  status: number;
}

function ReportForm({
  caseDetailId,
  userRole,
  onPublish,
  onClose,
  user,
  values,
  setFieldValue,
  fromViewer = false,
}: ReportFormProps) {
  
  const [caseDetail, setCaseDetail] = useState<CaseDetail>();

  const [formValues, setFormValues] = useState(new Report());

  const [reportTemplates, setReportTemplates] = useState<ReportTemplate[]>([]);

  const [reportTemplateOptions, setReportTemplateOptions] = useState<
    IDropdownOptions[]
  >([]);

  const [formLoading, setFormLoading] = useState(false);

  const [publishLoading, setPublishLoading] = useState(false);

  const [pdfLoading, setPdfLoading] = useState(false);

  const [wordLoading, setWordLoading] = useState(false);

  const [caseLoading, setCaseLoading] = useState(false);

  const debounceTimerRef: any = useRef(null);

  const [searchTerm, setSearchTerm] = useState("");

  const [voiceLoading, setVoiceLoading] = useState(false);

  const [content, setContent] = useState(""); // CKEditor content state
  // const recognitionRef = useRef<SpeechRecognition | null>(null); // Ref to manage SpeechRecognition instance
  const editorInstanceRef = useRef<any>(null);
  const [timer, setTimer] = useState(0); // Timer in seconds
  const [totalTimer, setTotalTimer] = useState(0); // Timer in seconds
  const [sessionTimer, setSessionTimer] = useState(0); // Timer in seconds
  const [isMuted, setIsMuted] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [aiReportLoading, setAiReportLoading] = useState(false);
  /** Until the first SSE chunk arrives, show prep overlay + Voxa tip; then hide so streaming HTML is readable. */
  const [aiReportShowPrepLoader, setAiReportShowPrepLoader] = useState(false);
  const [usgReportLoading, setUsgReportLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [copilotModalOpen, setCopilotModalOpen] = useState(false);
  const [copilotDraft, setCopilotDraft] = useState("");
  const [copilotLoading, setCopilotLoading] = useState(false);
  const [copilotSaving, setCopilotSaving] = useState(false);
  const [radAiOpen, setRadAiOpen] = useState(false);
  const [isVoiceToTextEnabled, setVoiceToTextEnabled] = useState(false);
  const [isAiReportingEnabled, setAiReportingEnabled] = useState(false);
  const [aiCreditsUsed, setAiCreditsUsed] = useState(0);
  const [aiTotalCredits, setAiTotalCredits] = useState(0);
  const [isAiReportingAllowed, setAiReportingAllowed] = useState(false);
  const [isVoiceToTextAllowed, setVoiceToTextAllowed] = useState(false);
  const [disableAiReporting, setDisableAiReporting] = useState(false);
  const [disableUsgReporting, setDisableUsgReporting] = useState(false);

  const [isAiDiagnosisEnabled, setAiDiagnosisEnabled] = useState(false);

  const [textareaValue, setTextareaValue] = useState("");
  const smartReportFindingsRef = useRef("");
  const [editorValue, setEditorValue] = useState("");
  const [initialAiDiagnosisValue, setInitialAiDiagnosisValue] = useState("");

  const [template, setTemplate] = useState("");
  const [doctorInput, setDoctorInput] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  const [selectedOrgan, setSelectedOrgan] = useState(null);
  const [subOptions, setSubOptions] = useState([]);
  const [organOptions, setOrganOptions] = useState<{ value: string; label: string }[]>([]);
  const [subOptionsMap, setSubOptionsMap] = useState<{ [key: string]: { value: string; label: string }[] }>({});

  useEffect(() => {
    if (isModalOpen) {
      smartReportFindingsRef.current = textareaValue;
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (caseDetail) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        handleFetchReportTemplates(searchTerm);
      }, 500);
    }
  }, [searchTerm]);

  const startSpeechRecognition = (
    values: FormikValues,
    setFieldValue: (field: string, value: any) => void
  ) => {
    // check if user breaches 5 minute threshold to use voice to text feature
    if (!isVoiceToTextAllowed) {
      Notification({
        message:
          "Voice to text feature time limit exceeded. Contact Support Team",
        type: NotificationTypes.ERROR,
      });
      return false;
    }
    let silenceTimeout: NodeJS.Timeout | null = null;
    setVoiceLoading(true);
    navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      let previousTranscript = "";
      if (!("MediaRecorder" in window)) {
        return alert("Browser not supported");
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm",
      });

      socket = new WebSocket(process.env.REACT_APP_VOICE_BASE_URL! + ApiRoutes.VOICE_TO_TEXT_ENABLE);

      socket.onopen = () => {
        setIsRunning(true);
        setIsMuted(false);
        Notification({
          message: "Speech detection activated!",
          type: NotificationTypes.SUCCESS,
        });
        mediaRecorder.addEventListener("dataavailable", async (event: any) => {
          if (event.data.size > 0 && socket!.readyState == 1) {
            socket!.send(event.data);
          }
        });
        mediaRecorder.start(1000);
        resetSilenceTimeout(); // Start silence timer
      };

      socket.onmessage = (message) => {
        const transcript = message.data;

        if (transcript) {
          // Append to CKEditor
          // const editorElement = document.querySelector(
          //     ".editor .ck-editor__editable"
          // );
          setTextareaValue((prevValue) => {
            const next = prevValue + " " + transcript;
            smartReportFindingsRef.current = next;
            return next;
          });

          // if (document.activeElement === editorElement) {
          //   appendAtCursor(transcript);
          // } else if (document.activeElement === textareaElement) {
          //   // If the textarea is focused, update its value
          //   setTextareaValue((prevValue) => prevValue + transcript);
          // } else {
          //   // Default to CKEditor if nothing else is focused
          //   appendToEnd(transcript);
          // }
        }
        resetSilenceTimeout();
      };

      // Function to append text at the cursor
      const appendAtCursor = (transcript: string) => {
        const editorInstance = editorInstanceRef.current;
        if (editorInstance) {
          editorInstance.model.change((writer: any) => {
            const selection = editorInstance.model.document.selection;
            const insertPosition = selection.focus; // Cursor position
            writer.insertText(transcript + " ", insertPosition);
          });
        }
      };

      // Function to append text at the end of CKEditor
      const appendToEnd = (transcript: string) => {
        const editorInstance = editorInstanceRef.current;
        if (editorInstance) {
          const currentContent = editorInstance.getData(); // Get current content
          let updatedContent = currentContent;

          // Check if the last character is a line break or paragraph tag
          if (
            updatedContent.endsWith("</p>") ||
            updatedContent.endsWith("<br>")
          ) {
            // If it ends with a line break or paragraph, append the transcript within the same block
            updatedContent =
              updatedContent.replace(/<\/p>$/, "") + " " + transcript;
          } else {
            // Otherwise, append the text with a space and keep it within the same paragraph
            updatedContent = updatedContent + " " + transcript;
          }

          // Update the editor content
          editorInstance.setContent(updatedContent);
        }
      };

      socket.onclose = () => {
        mediaRecorder.stop();
        Notification({
          message: "Speech detection ended!",
          type: NotificationTypes.SUCCESS,
        });
        // Stop all tracks in the MediaStream to release the microphone
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }
        if (silenceTimeout) {
          clearTimeout(silenceTimeout);
          silenceTimeout = null;
        }
        setIsMuted(true);
        setIsRunning(false);
        setSessionTimer(0);
      };

      socket.onerror = (error) => {
        console.log({ event: "onerror", error });
      };
    });

    function resetSilenceTimeout() {
      if (silenceTimeout) clearTimeout(silenceTimeout);
      silenceTimeout = setTimeout(() => {
        // alert(timer)
        // alert(sessionTimer);
        stopSpeechRecognition();
        // setTimer(0);
        setSessionTimer(0);
      }, 30000);
    }
  };

  function storeVoiceToTextSession() {
    ReportService.storeVoiceToTextSessionData(
      userRole,
      user.id,
      sessionTimerRef.current,
      caseDetail?.id,
      (response: any) => {
        setVoiceToTextEnabled(true);
        setTimer(response.data.used_credits);
        setTotalTimer(response.data.subscribed_credits);
        setIsMuted(true);
        setIsRunning(false);
        setSessionTimer(0);
        setVoiceToTextAllowed(response.data.allowed);
      },
      () => {},
      () => {}
    );
    setVoiceLoading(false);
  }

  function storeAiReportingSession() {
    ReportService.storeAiReportingSessionData(
      userRole,
      user.id,
      1,
      caseDetail?.id,
      (response: any) => {
        setAiCreditsUsed(response.data.used_credits);
        setAiTotalCredits(response.data.subscribed_credits);
        setAiReportingAllowed(response.data.allowed);
      },
      () => {},
      () => {}
    );
    setVoiceLoading(false);
  }

  const stopSpeechRecognition = () => {
    if (socket && socket.readyState !== 3) {
      socket.close();
      storeVoiceToTextSession();
    }
  };

  const removeBgColor = (text: string): string => {
    return text.replace(
      /<span[^>]*style=["'][^"']*background-color:\s*yellow;?[^"']*["'][^>]*>(.*?)<\/span>/gi,
      "$1"
    );
  };

  const styleTableElements = (text: string): string => {
    text= text.replace(
        /<table(?![^>]*style=)/g,
        '<table style="border:1px double #b3b3b3;"'
    );
    text= text.replace(
        /<th\b(?![^>]*style=)/g,
        '<th style="background-color:#fafafa;border:1px double #b3b3b3;"'
    );
    text= text.replace(
        /<td(?![^>]*style=)/g,
        '<td style="border:1px solid transparent;border-color: #d9d9d9"'
    );
    return text;
  };

  const smartReporting = (
    values: FormikValues,
    setFieldValue: (field: string, value: any) => void
  ) => {
    if (!isAiReportingAllowed) {
      Notification({
        message:
          "AI reporting feature time limit exceeded. Contact Support Team",
        type: NotificationTypes.ERROR,
      });
      return false;
    }
    setIsModalOpen(true);
    return true;
  };

  const openCopilotModal = useCallback(() => {
    if (!isAiReportingAllowed) {
      Notification({
        message:
          "AI reporting feature time limit exceeded. Contact Support Team",
        type: NotificationTypes.ERROR,
      });
      return;
    }
    setCopilotModalOpen(true);
    setCopilotLoading(true);
    ReportService.fetchAiReportingCopilot(
      userRole,
      (prompt) => {
        setCopilotDraft(prompt);
        setCopilotLoading(false);
      },
      (msg) => {
        Notification({ message: msg, type: NotificationTypes.ERROR });
        setCopilotLoading(false);
      }
    );
  }, [isAiReportingAllowed, userRole]);

  const saveCopilotDraft = useCallback(() => {
    setCopilotSaving(true);
    ReportService.saveAiReportingCopilot(
      userRole,
      copilotDraft,
      () => {
        setCopilotSaving(false);
        setCopilotModalOpen(false);
        Notification({
          message:
            "Your copilot instructions were saved. They apply to your future Smart Reports.",
          type: NotificationTypes.SUCCESS,
        });
      },
      (msg) => {
        setCopilotSaving(false);
        Notification({ message: msg, type: NotificationTypes.ERROR });
      }
    );
  }, [userRole, copilotDraft]);

  const generateGeminiReport = async (
    textContent: string,
    values: FormikValues
  ) => {
    try {
      stopSpeechRecognition();
      if (!isAiReportingAllowed) {
        Notification({
          message:
            "Voice to text feature time limit exceeded. Contact Support Team",
          type: NotificationTypes.ERROR,
        });
        return false;
      }
      const report = Object.assign(new Report(), values);
      if (report) {
        setAiReportLoading(true);
        setAiReportShowPrepLoader(true);
        setDisableUsgReporting(true);
        const editorInstance = editorInstanceRef.current;
        // Generate vs refine is decided by whether a report template is selected:
        //  • Template chosen → merge the findings into the current editor HTML
        //    (which already contains the template) refine-style, preserving layout.
        //  • No template → generate a fresh report from the findings only
        //    (freeform). Mirrors app-v2-staging's with/without-template behaviour.
        const isTemplateChosen =
          values?.reportTemplateId !== undefined &&
          values?.reportTemplateId !== null &&
          values?.reportTemplateId !== "";
        const currentEditorHtml =
          (typeof editorInstance?.getContent === "function"
            ? editorInstance.getContent()
            : report.content) || report.content || "";
        // Template mode sends the editor HTML to merge into; freeform sends none.
        const textContentForApi = isTemplateChosen ? currentEditorHtml : "";
        editorInstance.mode.set('readonly'); // Disable editor
        const editorContent = currentEditorHtml;
        editorInstance.setContent(".");
        ReportService.generateGeminiReport(
          report,
          textContentForApi,
          textContent,
          [],
          (generatedText: string) => {
            onPublish && onPublish();
            generatedText = generatedText.replace(/```html|```/g, "").trim();
            editorInstance.setContent(generatedText);
            editorInstance.mode.set('design');
            storeAiReportingSession();
            setAiReportLoading(false);
            setAiReportShowPrepLoader(false);
            setDisableUsgReporting(false);
          },
          (errorMessage?: string) => {
            editorInstance.mode.set('design'); // Enable editor
            Notification({
              message: errorMessage || "Smart Report generation failed",
              type: NotificationTypes.ERROR,
            });
            editorInstance.setContent(editorContent);
          },
          () => {
            editorInstance.mode.set('design');
            setAiReportLoading(false);
            setAiReportShowPrepLoader(false);
            setDisableUsgReporting(false);
            smartReportFindingsRef.current = "";
            setTextareaValue("");
          },
          {
            adminId: resolveAiReportingAdminId(userRole, user),
            useReportTemplate: isTemplateChosen,
            userRole,
            caseDetail,
            onStreamChunk: (acc: string) => {
              setAiReportShowPrepLoader(false);
              editorInstance.setContent(acc);
              if (typeof editorInstance.mode?.set === "function") {
                editorInstance.mode.set("readonly");
              }
            },
          }
        );
      }
    } catch (error) {
      console.error("Gemini AI reporting error");
    }
  };

  const handleDownloadReport = (
    status: boolean | undefined,
    fileType: DownloadFileTypeEnum,
    withLetterHead: boolean,
    reportId?: number
  ) => () => {
    if (caseDetail?.id) {
      setPdfLoading(true);
      if (status) {
        ReportService.downloadReport(
          fileType,
          caseDetail,
          withLetterHead,
          reportId ?? 0,
          (reportUrl: string) => {
            window.open(reportUrl, "_blank");
          },
          () => {},
          () => {
            setPdfLoading(false);
            setWordLoading(false);
          }
        );
      } else {
        const apiMethod =
          caseDetail?.status === CaseStatusEnum.TO_BE_APPROVED
            ? ReportService.previewApprovalReport
            : ReportService.previewReport;
        apiMethod(
          caseDetail?.id,
            reportId ?? 0,
          (data: any) => {
            const url = window.URL.createObjectURL(
              new Blob([data], { type: "application/pdf" })
            );
            const pdfWindow = window.open();
            if (pdfWindow) {
              pdfWindow.location.href = url;
              pdfWindow.document.title = "report";
            }
          },
          () => {},
          () => {
            setPdfLoading(false);
            setWordLoading(false);
          }
        );
      }
    }
  };

  const handleViewReport = (
      caseDetail: CaseDetail | undefined,
      reportId?: number | undefined,
  ) => {
    if (caseDetail?.id) {
      const apiMethod = ReportService.previewReport;
      apiMethod(
          caseDetail?.id,
          reportId ?? 0,
          (data: Blob) => {
            const fileURL = window.URL.createObjectURL(
                new Blob([data], { type: "application/pdf" })
            );
            const pdfWindow = window.open(fileURL, "_blank");
            if (pdfWindow) {
              pdfWindow.onload = () => {
                window.URL.revokeObjectURL(fileURL);
              };
            }
          },
          () => {},
          () => {
            setPdfLoading(false);
            setWordLoading(false);
          }
      );
    }
  };

  const handlePublishReport = (values: FormikValues) => () => {
    const report = Object.assign(new Report(), values);
    // @ts-ignore
    report.content = removeBgColor(report.content);
    report.content = setImageWidthFromFigure(report.content);
    report.content = styleTableElements(report.content);
    if (report && report.id) {
      setPublishLoading(true);
      ReportService.publishReport(
        report,
        (report: Report) => {
          setFormValues(report);
          onPublish && onPublish();
        },
        () => {},
        () => {
          setPublishLoading(false);
        }
      );
    }
  };

  const handleApproveAndPublishReport = (values: FormikValues) => () => {
    const report = Object.assign(new Report(), values);
    // @ts-ignore
    report.content = removeBgColor(report.content);
    report.content = setImageWidthFromFigure(report.content);
    report.content = styleTableElements(report.content);
    if (report && report.id) {
      setPublishLoading(true);
      ReportService.approveAndPublishReport(
        report,
        (report: Report) => {
          setFormValues(report);
          onPublish && onPublish();
        },
        () => {},
        () => {
          setPublishLoading(false);
        }
      );
    }
  };

  const handleSendForApproval = (values: FormikValues) => () => {
    const report = Object.assign(new Report(), values);
    // @ts-ignore
    report.content = removeBgColor(report.content);
    report.content = setImageWidthFromFigure(report.content);
    report.content = styleTableElements(report.content);
    if (report && report.id) {
      setPublishLoading(true);
      ReportService.sendForApproval(
        report,
        caseDetailId,
        (report: Report) => {
          setFormValues(report);
          onPublish && onPublish();
          onClose && onClose();
        },
        () => {},
        () => {
          setPublishLoading(false);
        }
      );
    }
  };

  const handleSubmit = (values: Report) => {
    const report = Object.assign(new Report(), values);
    // @ts-ignore
    report.content = removeBgColor(report.content);
    report.content = setImageWidthFromFigure(report.content);
    report.content = styleTableElements(report.content);
    report.caseDetailId = caseDetail?.id;
    setFormLoading(true);
    if (report.id) {
      ReportService.updateReport(
        report,
        () => {
          setFormValues(report);
        },
        () => {},
        () => {
          setFormLoading(false);
        }
      );
    } else {
      ReportService.createReport(
        report,
        (report: Report) => {
          setFormValues(report);
        },
        () => {},
        () => {
          setFormLoading(false);
        }
      );
    }
  };

  // @ts-ignore
  const caseDetails = [
    { label: "Patient Name", value: caseDetail?.patientName },
    {
      label: "Age / Gender",
      value: `${caseDetail?.age} / ${caseDetail?.gender}`,
    },
    {
      label: "Scan Type",
      value: caseDetail?.scanTypeName,
    },
    {
      label: "Body Parts",
      value: caseDetail?.caseBodyParts
        ?.map((caseBodyPart) => caseBodyPart.bodyPartName)
        .join(", "),
    },
    {
      label: "",
      value: formValues?.id ? (
        formValues?.status ? (
          <Popover
            destroyTooltipOnHide
            overlayClassName="admin-cases__assignment-popover"
            placement="bottomLeft"
            content={
              <div>
                <Menu mode="vertical" className="border-none">
                  <Menu.Item
                      className="navbar-item m-0"
                      key="view"
                      onClick={() =>
                          handleViewReport(
                              caseDetail,
                              formValues?.id
                          )
                      }
                  >
                    View Report
                  </Menu.Item>
                  <Menu.Item
                    className="navbar-item m-1"
                    key="with_letterhead"
                    onClick={handleDownloadReport(
                      formValues?.status,
                      DownloadFileTypeEnum.PDF,
                      true,
                      formValues?.id
                    )}
                  >
                    Download with Letterhead
                  </Menu.Item>
                  <Menu.Item
                    className="navbar-item m-2"
                    key="without_letterhead"
                    onClick={handleDownloadReport(
                      formValues?.status,
                      DownloadFileTypeEnum.PDF,
                      false,
                      formValues?.id
                    )}
                  >
                    Download without Letterhead
                  </Menu.Item>
                  <Menu.Item
                    className="navbar-item m-3"
                    key="as_letterhead"
                    onClick={handleDownloadReport(
                      formValues?.status,
                      DownloadFileTypeEnum.WORD,
                      false,
                      formValues?.id
                    )}
                  >
                    Download as Word
                  </Menu.Item>
                </Menu>
              </div>
            }
            title="Download"
            trigger="click"
          >
            <Button type="primary" loading={pdfLoading}>
              <React.Fragment>
                <DownloadOutlined className="mr-1" />
                Download
              </React.Fragment>
            </Button>
          </Popover>
        ) : (
          <Button
            type="primary"
            onClick={handleDownloadReport(
              formValues?.status,
              DownloadFileTypeEnum.PDF,
              false,
                formValues?.id
            )}
            loading={pdfLoading}
          >
            <React.Fragment>
              <EyeOutlined className="mr-1" />
              Preview PDF
            </React.Fragment>
          </Button>
        )
      ) : (
        ""
      ),
    },
  ];

  const handleFetchReportTemplates = (searchText: string) => {
    ReportTemplateService.fetchReportTemplates(
      searchText,
      caseDetail?.scanTypeId,
      (reportTemplates: ReportTemplate[]) => {
        setReportTemplates(reportTemplates);
        setReportTemplateOptions(
          getDropdownOptions(reportTemplates, "name", "id")
        );
      },
      () => {},
      () => {}
    );
  };

  const setVoiceAndAiFeature = () => {
    const featuresMap: Record<string, any> = {
      voice_to_text: {
        enabled: false,
        setter: setVoiceToTextEnabled,
        serviceCall: ReportService.getVoiceToTextInfo,
        updateState: (response: any) => {
          setTimer(response.data.used_credits);
          setTotalTimer(response.data.subscribed_credits);
          setSessionTimer(0);
          setVoiceToTextAllowed(response.data.allowed);
        },
      },
      ai_reporting: {
        enabled: false,
        setter: setAiReportingEnabled,
        serviceCall: ReportService.getAiReportingInfo,
        updateState: (response: any) => {
          setAiCreditsUsed(response.data.used_credits);
          setAiTotalCredits(response.data.subscribed_credits);
          setAiReportingAllowed(response.data.allowed);
        },
      },
    };

    const userFeatures =
      userRole === UserRoleEnum.ADMIN
        ? user.adminFeatures
        : user.doctorFeatures;

    userFeatures?.forEach((feature: any) => {
      if (featuresMap[feature.feature]) {
        featuresMap[feature.feature].enabled = true;
      }
    });

    Object.values(featuresMap).forEach(
      ({ enabled, setter, serviceCall, updateState }) => {
        if (enabled) {
          serviceCall(
            userRole,
            user.id,
            (response: any) => {
              setter(true);
              updateState(response);
            },
            () => {},
            () => {}
          );
        }
      }
    );
  };

  const fetchOrganOptions =  () => {
    try {
      ReportService.getOrganList(
          (response: OrganApiResponse) => {
            const options = response.data.map((item: any) => ({
              value: item.value.replace(/\s+/g, "_").toLowerCase(),
              label: item.value
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (c: string) => c.toUpperCase()),
            }));
            setOrganOptions(options);
          },
          (error: any) => {
            console.error("Failed to fetch organ list", error);
          },
          () => {
            console.log("Organ list fetch finished");
          }
      );

    } catch (error) {
      console.error("Failed to fetch organ options:", error);
    }
  };

  const fetchOrganPathologyDetails =  () => {
    try {
      ReportService.getOrganPathologyDetails(
          (response: any) => {
            setSubOptionsMap(response.data);
          },
          (error: any) => {
            console.error("Failed to fetch organ list", error);
          },
          () => {
            console.log("Organ list fetch finished");
          }
      );

    } catch (error) {
      console.error("Failed to fetch organ options:", error);
    }
  };

  useEffect(() => {
    if (caseDetail?.id) {
      setVoiceAndAiFeature();
      handleFetchReportTemplates("");
      if (aiDiagnosisTypes.includes(caseDetail?.scanTypeName as string)){
        fetchOrganOptions();
        fetchOrganPathologyDetails();
      }
      if (caseDetail?.status === CaseStatusEnum.TO_BE_APPROVED) {
        ReportService.showAdminReport(
          caseDetailId,
          (report: Report) => {
            setFormValues(report);
          },
          () => {
            setFormValues(new Report());
          },
          () => {}
        );
      } else {
        ReportService.showReport(
          caseDetailId,
          (report: Report) => {
            setFormValues(report);
          },
          () => {
            setFormValues(new Report());
          },
          () => {}
        );
      }
    }
  }, [caseDetail]);

  useEffect(() => {
    if (caseDetailId) {
      const showMethod =
        userRole === UserRoleEnum.ADMIN
          ? AdminCaseService.showAdminCase
          : DoctorCaseService.showDoctorCase;
      showMethod(
        caseDetailId,
        (caseDetail: CaseDetail) => {
          setCaseDetail(caseDetail);
        },
        () => {},
        () => {}
      );
    }
  }, [caseDetailId]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null; // Explicitly typing interval

    if (isRunning) {
      interval = setInterval(() => {
        setTimer((prev) => prev + 1);
        setSessionTimer((prev) => prev + 1);
        // if current session exceeds total subscribed time
        if (timer + sessionTimerRef.current >= totalTimer) {
          stopSpeechRecognition();
        }
      }, 1000);
    } else if (interval) {
      clearInterval(interval);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning]);

  const sessionTimerRef = useRef(sessionTimer);

  useEffect(() => {
    sessionTimerRef.current = sessionTimer;
  }, [sessionTimer]);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "auto"; // ✅ Re-enable scrolling
    } else {
      document.body.style.overflow = ""; // ✅ Reset when modal closes
    }
  }, [isModalOpen]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const generateUsgReport = (
      values: FormikValues,
      setFieldValue: (field: string, value: any) => void
  ) => {
    if (!isAiDiagnosisEnabled) {
      Notification({
        message:
            "AI reporting feature time limit exceeded. Contact Support Team",
        type: NotificationTypes.ERROR,
      });
      return false;
    }
    const report = Object.assign(new Report(), values);
    if (report.content === undefined) {
      Notification({
        message: "Please select a report template for generating USG report",
        type: NotificationTypes.ERROR,
      });
      return false;
    }

    setUsgReportLoading(true);
    setDisableAiReporting(true);
    const editorInstance = editorInstanceRef.current;
    editorInstance.mode.set('readonly'); // Disable editor
    const editorContent = report.content;
    editorInstance.setContent(".");

    ReportService.generateUsgReport(
        caseDetail?.id,
        report,
        report.content,
        (generatedText: string) => {
          onPublish && onPublish();
          generatedText = generatedText.replace(/```html|```/g, "").trim();
          editorInstance.setContent(generatedText);
          editorInstance.mode.set('design');
          storeAiReportingSession();
          setUsgReportLoading(false);
          setDisableAiReporting(false);
        },
        () => {
          editorInstance.mode.set('design'); // Enable editor
          Notification({
            message: "USG Report generation failed",
            type: NotificationTypes.ERROR,
          });
          editorInstance.setContent(editorContent);
        },
        () => {
          editorInstance.mode.set('design');
          setUsgReportLoading(false);
          setDisableAiReporting(false);
          smartReportFindingsRef.current = "";
          setTextareaValue("");
        }
    );

  };

  const handleOrganSelect = (value: any) => {
    setSelectedOrgan(value);

    // populate dependent options
    // @ts-ignore
    setSubOptions(subOptionsMap[value] || []);
    // your logic here
  };

  const handlePathologyDetail = (value: any) => {
    if(value !== undefined) {
      const editorInstance = editorInstanceRef.current;
      if (editorInstance) {
        editorInstance.model.change((writer: any) => {
          const selection = editorInstance.model.document.selection;
          const insertPosition = selection.focus; // Cursor position
          writer.insertText(value + " ", insertPosition);
        });
      }
    }

  }

  return (
    <div className="report-form">
      {caseLoading ? (
        <AppLoader loading={caseLoading} />
      ) : (
        <div>
          <Row gutter={[20, 20]}>
            {caseDetails?.map((caseDetail, i) => (
              <Col span={6} key={i}>
                <div className="report-form__label">{caseDetail?.label}</div>
                <div className="report-form__value">{caseDetail?.value}</div>
              </Col>
            ))}
          </Row>
          {!formValues?.status && (
            <Formik
              initialValues={formValues}
              onSubmit={handleSubmit}
              enableReinitialize
              validationSchema={reportFormValidation}
            >
              {({ values, errors, isValid, setFieldValue }) => {
                let screenWidth = window.innerWidth;
                return (
                  <Form>
                    <Row gutter={[0, 10]}>
                      <Col span={fromViewer ? 24 : 12}>
                        <DropdownField
                          allowClear
                          placeHolder="Select Template"
                          showSearch
                          title="Report Template"
                          name="reportTemplateId"
                          onSearch={(value) => {
                            setSearchTerm(value);
                          }}
                          onClear={() => {
                            setSearchTerm("");
                          }}
                          onChange={(value) => {
                            setFieldValue("reportTemplateId", value);
                            const template = reportTemplates.find(
                              (template) => template.id === value
                            );
                            if (template) {
                              setFieldValue(
                                "content",
                                values.content
                                  ? values?.content + "<br/>" + template.content
                                  : template.content
                              );
                            }
                          }}
                          options={reportTemplateOptions}
                        />
                      </Col>
                      { isAiDiagnosisEnabled ? (
                          aiDiagnosisTypes.includes(caseDetail?.scanTypeName as string) && (
                              <>
                                <Col span={12}>
                                </Col>
                                <Col span={7} style={{marginTop: -10}}>
                                  {/* First Dropdown - Organs */}
                                  <DropdownField
                                      allowClear
                                      placeHolder="Select Organ"
                                      showSearch
                                      title="Organ"
                                      name="organId"
                                      onChange={(value) => {
                                        setFieldValue("organId", value);
                                        handleOrganSelect(value);
                                      }}
                                      options={organOptions}
                                  />
                                </Col>
                                <Col span={15} style={{marginLeft: 5, marginTop: -10}}>
                                  {/* Second Dropdown - Dependent */}
                                  <DropdownField
                                      allowClear
                                      placeHolder="Select Detail"
                                      showSearch
                                      title="Pathology Detail"
                                      name="detailId"
                                      onChange={(value) => {
                                        setFieldValue("organId", value);
                                        handlePathologyDetail(value);
                                      }}
                                      options={subOptions}
                                      disabled={!selectedOrgan} // disable until organ is chosen
                                  />
                                </Col>
                              </>
                          )
                      ) : null
                      }
                      <Col span={24} className="report-form__ai-assist">
                        <React.Fragment>
                          {isAiReportingEnabled ? (
                            <>
                            { isAiDiagnosisEnabled ? (
                                aiDiagnosisTypes.includes(caseDetail?.scanTypeName as string) && (
                                <Button
                                    type="primary"
                                    loading={usgReportLoading}
                                    disabled={disableUsgReporting}
                                    onClick={() => {
                                      generateUsgReport(values, setFieldValue);
                                    }}
                                    style={{ marginRight: 5 }}
                                >
                                  Generate USG Report
                              </Button>
                            )) : null }
                              <Button
                                type="primary"
                                loading={aiReportLoading}
                                disabled={disableAiReporting}
                                onClick={() => {
                                  smartReporting(values, setFieldValue);
                                }}
                              >
                                Try Radiolinq AI Assist
                              </Button>

                              {/* Tooltip icon for how to use */}
                              <Tooltip
                                title={
                                  <div>
                                    <p>
                                      <strong>
                                        How to use Radiolinq AI Assist?
                                      </strong>
                                    </p>
                                    <ul style={{ paddingLeft: 16 }}>
                                      <li>
                                        Click on{" "}
                                        <em>Try Radiolinq AI Assist</em>
                                      </li>
                                      <li>
                                        Enter key findings / impression
                                      </li>
                                      <li>
                                        Optional: choose a report template above
                                        to merge findings into that layout
                                      </li>
                                      <li>
                                        Or leave template unselected for a full
                                        report from your findings only
                                      </li>
                                      <li>
                                        Click on <em>Generate Smart Report</em>
                                      </li>
                                    </ul>
                                  </div>
                                }
                                placement="top"
                              >
                                <QuestionCircleOutlined
                                  style={{
                                    fontSize: 16,
                                    marginLeft: 8,
                                    cursor: "pointer",
                                    color: "#1747E9",
                                  }}
                                />
                              </Tooltip>

                              <Tooltip title="Customize your copilot">
                                <Button
                                  icon={<SettingOutlined />}
                                  loading={copilotLoading}
                                  onClick={openCopilotModal}
                                  disabled={disableAiReporting}
                                  style={{
                                    marginLeft: 8,
                                    color: "#1747E9",
                                    borderColor: "rgba(23, 71, 233, 0.35)",
                                    background: "rgba(23, 71, 233, 0.06)",
                                  }}
                                />
                              </Tooltip>

                              <span
                                style={{
                                  marginLeft: "10px",
                                  marginRight: "10px",
                                }}
                              >
                                <span>
                                  {aiCreditsUsed} / {aiTotalCredits} credits
                                </span>
                              </span>
                              <Tooltip title="Chat with RadAI Assistant about this case">
                                <Button
                                  className="radai-trigger"
                                  icon={<RobotOutlined />}
                                  style={{ float: "right" }}
                                  onClick={() => setRadAiOpen(true)}
                                >
                                  RadAI Assistant
                                </Button>
                              </Tooltip>
                            </>
                          ) : null}
                        </React.Fragment>
                        {isAiReportingEnabled && (
                          <RadAiAssistant
                            visible={radAiOpen}
                            onClose={() => setRadAiOpen(false)}
                            userRole={userRole}
                            caseDetail={caseDetail}
                            getReportContent={() =>
                              ((editorInstanceRef.current?.getContent?.() as string) ||
                                values.content ||
                                "")}
                            onInsert={(html: string) => {
                              const inst = editorInstanceRef.current;
                              if (inst && typeof inst.insertContent === "function") {
                                inst.insertContent(html);
                                if (typeof inst.getContent === "function") {
                                  setFieldValue("content", inst.getContent());
                                }
                              } else {
                                setFieldValue(
                                  "content",
                                  (values.content || "") + html
                                );
                              }
                            }}
                          />
                        )}
                        {/*<React.Fragment>*/}
                        {/*  {isVoiceToTextEnabled ? (*/}
                        {/*      <React.Fragment>*/}
                        {/*        {" "}*/}
                        {/*        {}*/}
                        {/*        <Button*/}
                        {/*            type="primary"*/}
                        {/*            onClick={() => {*/}
                        {/*              if (voiceLoading) {*/}
                        {/*                stopSpeechRecognition();*/}
                        {/*              } else {*/}
                        {/*                startSpeechRecognition(*/}
                        {/*                    values,*/}
                        {/*                    setFieldValue*/}
                        {/*                );*/}
                        {/*                smartReporting(values, setFieldValue);*/}
                        {/*              }*/}
                        {/*            }}*/}
                        {/*        >*/}
                        {/*          {isMuted ? (*/}
                        {/*              <AudioMutedOutlined />*/}
                        {/*          ) : (*/}
                        {/*              <AudioOutlined />*/}
                        {/*          )}*/}
                        {/*        </Button>*/}

                        {/*        /!* Tooltip icon for how to use *!/*/}
                        {/*        <Tooltip*/}
                        {/*            title={*/}
                        {/*              <div>*/}
                        {/*                <p><strong>How to use Voice-to-Text?</strong></p>*/}
                        {/*                <ul style={{paddingLeft: 16}}>*/}
                        {/*                  <li>Use a microphone for better accuracy</li>*/}
                        {/*                  <li>Speak clearly and pause between words for better accuracy</li>*/}
                        {/*                  <li>Say <code>"comma"</code> → <code>,</code></li>*/}
                        {/*                  <li>Say <code>"period"</code> → <code>.</code></li>*/}
                        {/*                  <li>Say <code>"new line"</code> to start a new line</li>*/}
                        {/*                </ul>*/}
                        {/*              </div>*/}
                        {/*            }*/}
                        {/*            placement="top"*/}
                        {/*        >*/}
                        {/*          <QuestionCircleOutlined style={{  fontSize: 16, marginLeft: 8, cursor: 'pointer', color: '#1747E9' }} />*/}
                        {/*        </Tooltip>*/}

                        {/*        <span style={{ marginLeft: "10px", marginRight: "10px" }}>*/}
                        {/*        <span>*/}
                        {/*          {formatTime(timer)} / {formatTime(totalTimer)}*/}
                        {/*        </span>*/}
                        {/*      </span>*/}
                        {/*      </React.Fragment>*/}
                        {/*  ) : null}*/}
                        {/*</React.Fragment>*/}
                      </Col>

                      {isModalOpen && (
                        <>
                          <Input.TextArea
                            rows={4}
                            value={textareaValue}
                            onChange={(e) => {
                              const v = e.target.value;
                              smartReportFindingsRef.current = v;
                              setTextareaValue(v);
                            }}
                            placeholder="Enter your key findings or observations"
                          />
                          {isVoiceToTextEnabled ? (
                            <React.Fragment>
                              {" "}
                              {}
                              <Button
                                type="primary"
                                onClick={() => {
                                  if (voiceLoading) {
                                    stopSpeechRecognition();
                                  } else {
                                    startSpeechRecognition(
                                      values,
                                      setFieldValue
                                    );
                                    smartReporting(values, setFieldValue);
                                  }
                                }}
                              >
                                {isMuted ? (
                                  <AudioMutedOutlined />
                                ) : (
                                  <AudioOutlined />
                                )}
                              </Button>
                              {/* Tooltip icon for how to use */}
                              <Tooltip
                                title={
                                  <div>
                                    <p>
                                      <strong>How to use Voice-to-Text?</strong>
                                    </p>
                                    <ul style={{ paddingLeft: 16 }}>
                                      <li>
                                        Use a microphone for better accuracy
                                      </li>
                                      <li>
                                        Speak clearly and pause between words
                                        for better accuracy
                                      </li>
                                      <li>
                                        Say <code>"comma"</code> →{" "}
                                        <code>,</code>
                                      </li>
                                      <li>
                                        Say <code>"period"</code> →{" "}
                                        <code>.</code>
                                      </li>
                                    </ul>
                                  </div>
                                }
                                placement="top"
                              >
                                <QuestionCircleOutlined
                                  style={{
                                    marginTop: 10,
                                    fontSize: 16,
                                    marginLeft: 8,
                                    cursor: "pointer",
                                    color: "#1747E9",
                                  }}
                                />
                              </Tooltip>
                              <span
                                style={{
                                  marginTop: "7px",
                                  marginLeft: "10px",
                                  marginRight: "10px",
                                }}
                              >
                                <span>
                                  {formatTime(timer)} / {formatTime(totalTimer)}
                                </span>
                              </span>
                            </React.Fragment>
                          ) : null}
                          <Button
                            key="submit"
                            type="primary"
                            style={{ marginRight: "10px" }}
                            onClick={() => {
                              const findings = (
                                smartReportFindingsRef.current || textareaValue
                              ).trim();
                              if (!findings) {
                                alert(
                                  "Please enter key findings or observations for generating report."
                                );
                              } else {
                                generateGeminiReport(
                                  findings,
                                  values
                                ).then((r) => {});
                                setIsModalOpen(false);
                              }
                            }}
                          >
                            Generate Smart Report
                          </Button>
                          <Button
                            key="cancel"
                            onClick={() => {
                              setIsModalOpen(false);
                              stopSpeechRecognition();
                            }}
                            style={{ marginRight: "10px" }}
                          >
                            Cancel
                          </Button>
                        </>
                      )}
                      <Col span={24}>
                        <Editor
                          error={errors.content as string}
                          value={values.content || ""}
                          onChangeHandler={(content) => {
                            setFieldValue("content", content);
                          }}
                          editorInstanceRef={editorInstanceRef}
                          readOnly={aiReportLoading || usgReportLoading}
                          aiReportStreaming={
                            aiReportLoading && !aiReportShowPrepLoader
                          }
                        />
                        {(usgReportLoading ||
                          (aiReportLoading && aiReportShowPrepLoader)) && (
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              background: "rgba(255,255,255,0.6)",
                              zIndex: 10,
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              pointerEvents: "none", // Allows click-through for readOnly editor
                            }}
                          >
                            <Spin
                              size="large"
                              style={{ color: "#1747E9" }}
                              tip="Voxa is preparing your radiology report"
                            />
                          </div>
                        )}
                      </Col>
                    </Row>
                    <div className="report-form__submit-wrapper text-right">
                      {values?.id &&
                        !values.status &&
                        (user?.approvalRequired ? (
                          <Button
                            className="mr-2"
                            type="primary"
                            htmlType="button"
                            loading={publishLoading}
                            disabled={publishLoading}
                            onClick={handleSendForApproval(values)}
                          >
                            Send for Approval
                          </Button>
                        ) : caseDetail?.status ===
                            CaseStatusEnum.TO_BE_APPROVED &&
                          userRole === UserRoleEnum.ADMIN ? (
                          <Button
                            className="mr-2"
                            type="primary"
                            htmlType="button"
                            loading={publishLoading}
                            disabled={publishLoading}
                            onClick={handleApproveAndPublishReport(values)}
                          >
                            Approve and Publish Report
                          </Button>
                        ) : (
                          <Button
                            className="mr-2"
                            type="primary"
                            htmlType="button"
                            loading={publishLoading}
                            disabled={publishLoading}
                            onClick={handlePublishReport(values)}
                          >
                            Save & Publish Report
                          </Button>
                        ))}
                      {!values.status && (
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={formLoading}
                          disabled={!isValid || formLoading}
                        >
                          Save Report
                        </Button>
                      )}
                    </div>
                  </Form>
                );
              }}
            </Formik>
          )}
        </div>
      )}
      <Modal
        title="Customize your copilot"
        visible={copilotModalOpen}
        onCancel={() => !copilotSaving && setCopilotModalOpen(false)}
        onOk={saveCopilotDraft}
        okText="Save"
        confirmLoading={copilotSaving}
        destroyOnClose
        width={560}
        maskClosable={!copilotSaving}
        okButtonProps={{ disabled: copilotLoading }}
      >
        <p style={{ color: "#595959", marginBottom: 12 }}>
          How do you want Voxa to generate your reports? Describe formatting,
          structure, tone, or standing rules. Saved instructions apply only to
          your account and take highest priority when they conflict with generic
          guidance.
        </p>
        <Input.TextArea
          rows={10}
          maxLength={200000}
          value={copilotDraft}
          onChange={(e) => setCopilotDraft(e.target.value)}
          placeholder='e.g. "Number all impression lines; keep Findings as short paragraphs; prefer British spelling."'
          disabled={copilotLoading || copilotSaving}
        />
        {copilotLoading ? (
          <div style={{ textAlign: "center", padding: 16 }}>
            <Spin />
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

export default AuthContainer(ReportForm);
